import * as React from "react";
import { cx } from "../internal/cx.js";

/**
 * The canonical Cakewalk progress indicator. Renders an equal-width row
 * of segmented bars, one per step. Only the active step's label is
 * rendered visually; all others stay in the DOM for screen readers and
 * stable layout but are hidden.
 *
 * State per segment:
 *   - completed  → Uniform Blue slash segment, label hidden
 *   - current    → Blue 200 slash segment, Ink semibold label
 *   - upcoming   → Blue 50 slash segment, label hidden
 *
 * Domain step indicators with numbered circles + connectors (e.g.,
 * `PaymentStepIndicator`) are a different pattern; they are NOT
 * consolidated into this component.
 */

export interface StepIndicatorStep {
  /** Stable React key + payload passed to `onStepClick`. */
  id: string;
  /** Short label shown beneath the segment. */
  label: React.ReactNode;
}

export interface StepIndicatorProps
  extends Omit<React.HTMLAttributes<HTMLOListElement>, "children" | "onClick"> {
  steps: StepIndicatorStep[];
  /** Zero-based index of the active step. */
  currentStep: number;
  /**
   * Optional click handler. When provided, completed and current steps
   * become focusable buttons. Upcoming steps stay disabled.
   */
  onStepClick?: (stepId: string, stepIndex: number) => void;
  /** Accessible label for the progress group. Default `"Progress"`. */
  ariaLabel?: string;
}

export const StepIndicator = React.forwardRef<HTMLOListElement, StepIndicatorProps>(
  function StepIndicator(
    { ariaLabel = "Progress", className, currentStep, onStepClick, steps, ...props },
    ref,
  ) {
    const totalSteps = steps.length;
    const safeCurrent = Math.max(0, Math.min(currentStep, totalSteps - 1));

    return (
      <ol
        ref={ref}
        aria-label={ariaLabel}
        className={cx("cw-v2-step-indicator", className)}
        {...props}
      >
        {steps.map((step, index) => {
          const state =
            index < safeCurrent ? "complete" : index === safeCurrent ? "current" : "upcoming";
          const isClickable = Boolean(onStepClick) && state !== "upcoming";

          const body = (
            <>
              <span
                className={cx("cw-v2-step-indicator__bar", `cw-v2-step-indicator__bar--${state}`)}
                aria-hidden
              />
              <span
                className={cx(
                  "cw-v2-step-indicator__label",
                  `cw-v2-step-indicator__label--${state}`,
                )}
              >
                {step.label}
              </span>
            </>
          );

          return (
            <li
              key={step.id}
              className={cx("cw-v2-step-indicator__item", `cw-v2-step-indicator__item--${state}`)}
              aria-current={state === "current" ? "step" : undefined}
            >
              {isClickable ? (
                <button
                  type="button"
                  className="cw-v2-step-indicator__trigger"
                  onClick={() => onStepClick?.(step.id, index)}
                >
                  {body}
                </button>
              ) : (
                <span className="cw-v2-step-indicator__trigger cw-v2-step-indicator__trigger--static">
                  {body}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    );
  },
);
StepIndicator.displayName = "StepIndicator";
